// Who is actually on the page, and who is only furniture.
//
// Live report — Nicole, 2026-08-10 15:44 EDT: "it doesn't like to bring in
// other chars even if they're in the side bar ... Trix is in a scene in my rp
// section. And I tried to prompt it multiple times to get a Trix included
// response but it didn't. It either tried to brush past the scene or have her
// not say anything rather than play out a scene."
//
// Measured on her story b52aeed2 (291 messages, cast cruz/ford/red/trix,
// playing as nicole_human): across 148 AI messages the name "Red" appears
// ZERO times in any case, and Trix has no attributed dialogue at all in the
// first 118. The cast reached the model — every NPC got a full identity
// anchor in the prompt. What went wrong was weighting: the prompt named the
// anchor character (castNow[0], Cruz) 19 times, handed him 60 voice samples
// against 5 for each of the others, and then told the model "Not every
// character needs to speak or do something in EVERY response". The model took
// that opt-out for everyone except the anchor, permanently.
//
// So the prompt has to say who has gone quiet, by name. This module measures
// it from the transcript rather than assuming.
//
// Detection caveats, both real and both designed around:
//   - The anchor character speaks through pronouns ("He turned, '…'"), so
//     name+verb matching UNDER-detects them. The anchor is therefore counted
//     active by construction, from the message's own charId.
//   - A name near a quote is often someone being spoken TO, not speaking
//     ("moving past Trix like she was furniture. 'I'll walk you.'"). Hand-
//     checking three such hits on Nicole's story is what caught this. Only
//     name-adjacent speech-verb attribution counts, and each match position
//     is counted once.

const SPEECH_VERBS =
  'said|asked|snapped|hissed|growled|whispered|muttered|laughed|added|replied|' +
  'spat|sneered|shot back|called|answered|breathed|offered|cut in|drawled|' +
  'purred|barked|murmured|repeated|continued|insisted|admitted|countered';

const escapeRe = (s) => String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export const firstNameOf = (name) => String(name || '').trim().split(/\s+/)[0] || '';

// A character is "mentioned" when their first name appears word-bounded.
function isMentioned(text, first) {
  return new RegExp(`\\b${escapeRe(first)}\\b`, 'i').test(text);
}

// A character "speaks" when their name carries a speech verb, or a quote is
// tagged with their name. Positions are deduped so one line counts once.
function spokenLineCount(text, first) {
  const n = escapeRe(first);
  const patterns = [
    // Trix finally said, "…"   /   Trix, voice sharp, snapped
    new RegExp(`\\b${n}\\b[^.!?"]{0,40}?\\b(?:${SPEECH_VERBS})\\b`, 'gi'),
    // "…" said Trix
    new RegExp(`"[^"]{2,}"[\\s,.]{0,3}(?:${SPEECH_VERBS})\\s+${n}\\b`, 'gi'),
    // "…" Trix said
    new RegExp(`"[^"]{2,}"[\\s,.]{0,3}${n}\\b[^.!?"]{0,25}?\\b(?:${SPEECH_VERBS})\\b`, 'gi'),
  ];
  const hits = new Set();
  for (const re of patterns) {
    let m;
    while ((m = re.exec(text)) !== null) {
      hits.add(m.index);
      if (m.index === re.lastIndex) re.lastIndex++; // zero-width guard
    }
  }
  return hits.size;
}

/**
 * Measure who has been present in the recent transcript.
 *
 * @param history  chronological messages: { text, isPlayer, charId }
 * @param cast     [{ id, name }] — the NPCs the AI may voice (player excluded
 *                 by the caller; this function does not guess who the player is)
 * @param opts     { windowSize = 12 } AI messages to look back over
 * @returns { active, silent, absent, windowSize, aiTurns }
 *          active — has spoken; silent — named but never speaks; absent — not
 *          named at all. Each entry is { id, name, mentions, lines, lastSpokeTurnsAgo }.
 */
export function measureCastPresence(history, cast, opts = {}) {
  const windowSize = Number.isFinite(opts.windowSize) ? opts.windowSize : 12;
  const aiMsgs = (history || []).filter((m) => m && !m.isPlayer);
  const win = windowSize > 0 ? aiMsgs.slice(-windowSize) : aiMsgs;

  const active = [];
  const silent = [];
  const absent = [];

  for (const c of cast || []) {
    if (!c?.id || !c?.name) continue;
    const first = firstNameOf(c.name);
    if (!first) continue;

    let mentions = 0;
    let lines = 0;
    let lastSpokeIdx = -1;

    win.forEach((m, i) => {
      const text = String(m?.text || '');
      if (!text) return;
      if (isMentioned(text, first)) mentions++;
      // The reply is filed under this character — they held the pen for it.
      const anchored = m?.charId && m.charId === c.id;
      const spoke = spokenLineCount(text, first);
      if (anchored || spoke > 0) {
        lines += anchored ? Math.max(1, spoke) : spoke;
        lastSpokeIdx = i;
      }
    });

    const entry = {
      id: c.id,
      name: c.name,
      mentions,
      lines,
      lastSpokeTurnsAgo: lastSpokeIdx >= 0 ? win.length - 1 - lastSpokeIdx : null,
    };
    if (lines > 0) active.push(entry);
    else if (mentions > 0) silent.push(entry);
    else absent.push(entry);
  }

  return { active, silent, absent, windowSize: win.length, aiTurns: aiMsgs.length };
}

/**
 * Turn a presence measurement into a prompt directive naming who has gone
 * quiet. Returns '' when the whole cast is active — no directive, no noise.
 */
export function buildCastPresenceDirective(presence, opts = {}) {
  if (!presence) return '';
  const { silent = [], absent = [], windowSize = 0 } = presence;
  const quiet = [...absent, ...silent];
  if (!quiet.length || !windowSize) return '';

  const describe = (e, kind) =>
    kind === 'absent'
      ? `${e.name} — not present at all in the last ${windowSize} turns`
      : `${e.name} — named ${e.mentions} time${e.mentions === 1 ? '' : 's'} but has not spoken a line`;

  const lines = [
    ...absent.map((e) => `• ${describe(e, 'absent')}`),
    ...silent.map((e) => `• ${describe(e, 'silent')}`),
  ];

  return `\n\nCAST PRESENCE — MANDATORY, NOT A SUGGESTION.
These characters are cast in this scene and have gone quiet:
${lines.join('\n')}
At least one of them must take a real turn in THIS response: a line of their own dialogue plus an action that changes something. Being described from the outside does not count — "every eye turned to ${quiet[0].name}", "${quiet[0].name}'s jaw worked" and similar are exactly the failure being corrected here. They speak, in their own voice, on their own initiative.
Use the voice samples and profile for that character, not the anchor character's. If two or more are listed, you do not have to use them all this turn — but nobody may stay silent for another ${Math.max(2, Math.ceil(windowSize / 2))} turns.
If someone genuinely cannot be in this scene because the story moved them elsewhere, write them leaving or being absent ONCE, explicitly, and then treat them as offstage instead of listing them as present.${opts.playerLabel ? `\nThis never applies to ${opts.playerLabel}'s own character — you still never write for them.` : ''}`;
}
